import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/common';
import statementService from '../services/statement.service';
import { DashboardData, DashboardFacturaPendiente, DashboardPagoReciente, DashboardTopProducto, DashboardTopMarca, InvoiceLine } from '../types';
import './Dashboard.css';

type PeriodoTipo = 'mes_actual' | 'mes_anterior';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Estado para filtros de periodo
  const [periodoTipo, setPeriodoTipo] = useState<PeriodoTipo>('mes_actual');

  // Estado para modal de detalle de factura
  const [selectedInvoice, setSelectedInvoice] = useState<DashboardFacturaPendiente | null>(null);
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);

  // Función para calcular fechas según el periodo
  const calcularFechas = useCallback((tipo: PeriodoTipo) => {
    const now = new Date();
    let startDate = '';
    let endDate = '';

    switch (tipo) {
      case 'mes_actual':
        // Del día 1 del mes actual hasta hoy
        startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        endDate = now.toISOString().split('T')[0]; // Hoy
        break;
      case 'mes_anterior':
        // Mes anterior completo (día 1 al último día del mes anterior)
        const primerDiaMesAnterior = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const ultimoDiaMesAnterior = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = primerDiaMesAnterior.toISOString().split('T')[0];
        endDate = ultimoDiaMesAnterior.toISOString().split('T')[0];
        break;
    }

    return { startDate, endDate };
  }, []);

  const loadDashboard = useCallback(async (tipo?: PeriodoTipo) => {
    try {
      setIsLoading(true);
      setError(null);

      const periodoActual = tipo || periodoTipo;
      const { startDate, endDate } = calcularFechas(periodoActual);

      // Siempre enviar las fechas del periodo seleccionado
      const result = await statementService.getDashboard({
        startDate: startDate,
        endDate: endDate
      });

      setDashboard(result.data);
      if (result.warning) {
        setWarning(result.warning);
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  }, [periodoTipo, calcularFechas]);

  useEffect(() => {
    loadDashboard();
  }, []);

  // Manejar cambio de periodo
  const handlePeriodoChange = (tipo: PeriodoTipo) => {
    setPeriodoTipo(tipo);
    loadDashboard(tipo);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Función para mostrar valores o placeholder
  const displayValue = (value: string | number | boolean | undefined | null): string => {
    if (value === null || value === undefined || value === '' || value === 0 || value === '0' || value === false || value === 'false') {
      return 'No disponible';
    }
    return String(value);
  };

  const getEstadoClass = (estado: string): string => {
    switch (estado) {
      case 'paid':
        return 'estado-pagado';
      case 'partial':
        return 'estado-parcial';
      case 'not_paid':
      default:
        return 'estado-pendiente';
    }
  };

  const getEstadoLabel = (estado: string): string => {
    switch (estado) {
      case 'paid':
        return 'Pagada';
      case 'partial':
        return 'Parcial';
      case 'not_paid':
      default:
        return 'Pendiente';
    }
  };

  // Obtener nombre del periodo para mostrar
  const getNombrePeriodo = (): string => {
    const now = new Date();
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    switch (periodoTipo) {
      case 'mes_actual':
        return `1 - ${now.getDate()} ${meses[now.getMonth()]} ${now.getFullYear()}`;
      case 'mes_anterior':
        // Mes anterior completo
        const mesAnteriorIndex = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const añoMesAnterior = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const ultimoDia = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        return `1 - ${ultimoDia} ${meses[mesAnteriorIndex]} ${añoMesAnterior}`;
      default:
        return '';
    }
  };

  // Función para ver detalle de factura
  const handleVerDetalle = async (factura: DashboardFacturaPendiente) => {
    setSelectedInvoice(factura);
    setLoadingLines(true);
    try {
      const result = await statementService.getInvoiceDetail(factura.id);
      setInvoiceLines(result.lines);
    } catch (err) {
      console.error('Error cargando detalle de factura:', err);
      setInvoiceLines([]);
    } finally {
      setLoadingLines(false);
    }
  };

  // Cerrar modal
  const closeModal = () => {
    setSelectedInvoice(null);
    setInvoiceLines([]);
  };

  if (isLoading) {
    return (
      <div className="dashboard dashboard-loading">
        {/* Skeleton del header del cliente */}
        <div className="client-header skeleton-animate">
          <div className="skeleton-circle" style={{ width: 64, height: 64 }}></div>
          <div className="client-info">
            <div className="skeleton-text" style={{ width: '60%', height: 24 }}></div>
            <div className="skeleton-text" style={{ width: '40%', height: 16, marginTop: 8 }}></div>
          </div>
        </div>

        {/* Skeleton de las stats cards */}
        <div className="stats-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="stat-card skeleton-card">
              <div className="skeleton-icon"></div>
              <div className="stat-content">
                <div className="skeleton-text" style={{ width: '50%', height: 14 }}></div>
                <div className="skeleton-text" style={{ width: '70%', height: 28, marginTop: 8 }}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Mensaje de carga */}
        <div className="loading-message">
          <div className="loading-spinner-small"></div>
          <span>Cargando información...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <Button onClick={() => loadDashboard()}>Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Warning Banner */}
      {warning && (
        <div className="warning-banner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{warning}</span>
        </div>
      )}

      {/* Client Info Header con Resumen Financiero Integrado */}
      <div className="client-profile-card">
        {/* Parte superior: Datos del cliente */}
        <div className="client-profile-header">
          <div className="client-avatar-section">
            <div className="client-avatar-lg">
              {dashboard?.cliente.nombre.charAt(0).toUpperCase()}
            </div>
            <div className="client-status-indicator"></div>
          </div>
          <div className="client-details">
            <h2 className="client-name-lg">{dashboard?.cliente.nombre}</h2>
            <div className="client-contact-info">
              <span className="contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                {displayValue(dashboard?.cliente.email)}
              </span>
              {dashboard?.cliente.telefono && dashboard.cliente.telefono !== '0' && (
                <span className="contact-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  {dashboard?.cliente.telefono}
                </span>
              )}
              {dashboard?.cliente.ejecutivo && (
                <span className="contact-item ejecutivo">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <strong>Ejecutivo:</strong> {dashboard?.cliente.ejecutivo}
                </span>
              )}
            </div>
          </div>
          {dashboard?.cliente.rfc && typeof dashboard.cliente.rfc === 'string' && dashboard.cliente.rfc !== '0' && (
            <div className="client-rfc-badge">
              <span className="rfc-label">RFC</span>
              <span className="rfc-value">{dashboard.cliente.rfc}</span>
            </div>
          )}
        </div>

        {/* Separador visual */}
        <div className="profile-divider"></div>

        {/* Parte inferior: Resumen financiero */}
        <div className="financial-summary">
          <div className="financial-item saldo-pendiente">
            <div className="financial-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div className="financial-content">
              <span className="financial-label">Saldo Pendiente</span>
              <span className="financial-value danger">{formatCurrency(dashboard?.resumen.saldoPendiente || 0)}</span>
            </div>
          </div>

          <div className="financial-item linea-credito">
            <div className="financial-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <div className="financial-content">
              <span className="financial-label">Línea de Crédito</span>
              <span className="financial-value">{formatCurrency(dashboard?.resumen.lineaCredito || 0)}</span>
            </div>
          </div>

          <div className="financial-item credito-disponible">
            <div className="financial-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div className="financial-content">
              <span className="financial-label">Crédito Disponible</span>
              <span className="financial-value success">{formatCurrency(dashboard?.resumen.creditoDisponible || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Period Selector */}
      <div className="period-selector">
        <div className="period-buttons">
          <button
            className={`period-btn ${periodoTipo === 'mes_actual' ? 'active' : ''}`}
            onClick={() => handlePeriodoChange('mes_actual')}
          >
            Mes Actual
          </button>
          <button
            className={`period-btn ${periodoTipo === 'mes_anterior' ? 'active' : ''}`}
            onClick={() => handlePeriodoChange('mes_anterior')}
          >
            Mes Anterior
          </button>
        </div>
        <span className="period-label">{getNombrePeriodo()}</span>
      </div>

      {/* Stats Cards - Solo Compras y Pagos del Periodo */}
      <div className="stats-grid stats-grid-2">
        <div className="stat-card">
          <div className="stat-icon stat-icon-blue">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Compras del Periodo</span>
            <span className="stat-value">{formatCurrency(dashboard?.resumen.totalCompras || 0)}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-green">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">Pagado del Periodo</span>
            <span className="stat-value text-success">{formatCurrency(dashboard?.resumen.totalPagado || 0)}</span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-content">
        {/* Pending Invoices Table */}
        <div className="dashboard-card invoices-card">
          <div className="card-header">
            <h3 className="card-title">Facturas Pendientes</h3>
            <Button
              variant="ghost"
              size="small"
              onClick={() => navigate('/historial')}
            >
              Ver todas
            </Button>
          </div>

          {dashboard?.facturasPendientes && dashboard.facturasPendientes.length > 0 ? (
            <div className="table-container">
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Fecha</th>
                    <th>Vencimiento</th>
                    <th>Total</th>
                    <th>Pendiente</th>
                    <th>Estado</th>
                    <th>Accion</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.facturasPendientes.slice(0, 5).map((factura: DashboardFacturaPendiente) => (
                    <tr key={factura.id}>
                      <td className="folio-cell" data-label="Folio">{factura.folio}</td>
                      <td data-label="Fecha">{formatDate(factura.fecha)}</td>
                      <td data-label="Vencimiento">{formatDate(factura.fechaVencimiento)}</td>
                      <td className="amount-cell" data-label="Total">{formatCurrency(factura.total)}</td>
                      <td className="amount-cell text-error" data-label="Pendiente">{formatCurrency(factura.saldoPendiente)}</td>
                      <td data-label="Estado">
                        <span className={`estado-badge ${getEstadoClass(factura.estado)}`}>
                          {getEstadoLabel(factura.estado)}
                        </span>
                      </td>
                      <td data-label="Accion">
                        <button
                          className="btn-ver-detalle"
                          onClick={() => handleVerDetalle(factura)}
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p>No hay facturas pendientes</p>
            </div>
          )}
        </div>

        {/* Recent Payments */}
        <div className="dashboard-card payments-card">
          <div className="card-header">
            <h3 className="card-title">
              Pagos y Abonos del Periodo
              {dashboard?.pagosRecientes && dashboard.pagosRecientes.length > 0 && (
                <span className="payments-count">{dashboard.pagosRecientes.length}</span>
              )}
            </h3>
          </div>

          {dashboard?.pagosRecientes && dashboard.pagosRecientes.length > 0 ? (
            <div className="payments-list-wrapper">
              <div className="payments-list">
                {dashboard.pagosRecientes.map((pago: DashboardPagoReciente) => (
                  <div key={pago.id} className={`payment-item ${pago.esNotaCredito ? 'payment-credit-note' : ''}`}>
                    <div className={`payment-icon ${pago.esNotaCredito ? 'icon-credit-note' : ''}`}>
                      {pago.esNotaCredito ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="12" y1="18" x2="12" y2="12" />
                          <line x1="9" y1="15" x2="15" y2="15" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
                          <path d="M16 12l-4-4-4 4M12 16V8" />
                        </svg>
                      )}
                    </div>
                    <div className="payment-info">
                      <span className="payment-ref">{pago.referencia}</span>
                      <span className="payment-date">{formatDate(pago.fecha)}</span>
                    </div>
                    <div className="payment-amount">
                      <span className={`amount ${pago.esNotaCredito ? 'text-orange' : 'text-success'}`}>
                        +{formatCurrency(pago.monto)}
                      </span>
                      <span className={`payment-type ${pago.esNotaCredito ? 'type-credit-note' : ''}`}>
                        {pago.tipo}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
              <p>No hay pagos ni abonos en este periodo</p>
            </div>
          )}
        </div>
      </div>

      {/* Top 5 Productos y Top 5 Marcas - Grid */}
      <div className="charts-grid">
        {/* Top 5 Productos */}
        {dashboard?.topProductos && dashboard.topProductos.length > 0 && (
          <div className="dashboard-card top-products-chart">
            <div className="chart-header">
              <div className="chart-title-section">
                <div className="chart-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 20V10M12 20V4M6 20v-6" />
                  </svg>
                </div>
                <div>
                  <h3 className="chart-title">Top 5 Productos</h3>
                  <p className="chart-subtitle">Productos más comprados (últimos 3 meses)</p>
                </div>
              </div>
            </div>

            <div className="horizontal-bar-chart">
              {(() => {
                const topProductos = dashboard.topProductos || [];
                const maxCantidad = topProductos[0]?.cantidadTotal || 1;

                return topProductos.slice(0, 5).map((producto: DashboardTopProducto, index: number) => {
                  const porcentaje = (producto.cantidadTotal / maxCantidad) * 100;
                  const getRankClass = () => {
                    if (index === 0) return 'gold';
                    if (index === 1) return 'silver';
                    if (index === 2) return 'bronze';
                    return 'default';
                  };

                  return (
                    <div
                      key={producto.id}
                      className={`chart-bar-row ${getRankClass()}`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="bar-label">
                        <span className="bar-rank">#{index + 1}</span>
                        <span className="bar-product-name" title={producto.nombre}>
                          {producto.nombre}
                        </span>
                      </div>
                      <div className="bar-track">
                        <div
                          className={`bar-fill ${getRankClass()}`}
                          style={{
                            width: `${porcentaje}%`,
                            animationDelay: `${index * 0.08}s`
                          }}
                        >
                          <span className="bar-value">{Math.round(producto.cantidadTotal)}</span>
                        </div>
                      </div>
                      <div className="bar-stats">
                        <span className="stat-orders">{producto.vecesComprado} compras</span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Top 5 Marcas */}
        {dashboard?.topMarcas && dashboard.topMarcas.length > 0 && (
          <div className="dashboard-card top-brands-chart">
            <div className="chart-header">
              <div className="chart-title-section">
                <div className="chart-icon brand-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                    <line x1="7" y1="7" x2="7.01" y2="7" />
                  </svg>
                </div>
                <div>
                  <h3 className="chart-title">Top 5 Marcas</h3>
                  <p className="chart-subtitle">Marcas más compradas (últimos 3 meses)</p>
                </div>
              </div>
            </div>

            <div className="horizontal-bar-chart">
              {(() => {
                const topMarcas = dashboard.topMarcas || [];
                const maxCantidad = topMarcas[0]?.cantidadTotal || 1;

                return topMarcas.slice(0, 5).map((marca: DashboardTopMarca, index: number) => {
                  const porcentaje = (marca.cantidadTotal / maxCantidad) * 100;
                  const getRankClass = () => {
                    if (index === 0) return 'gold';
                    if (index === 1) return 'silver';
                    if (index === 2) return 'bronze';
                    return 'default';
                  };

                  return (
                    <div
                      key={marca.id || index}
                      className={`chart-bar-row brand-row ${getRankClass()}`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="bar-label">
                        <span className="bar-rank">#{index + 1}</span>
                        <span className="bar-product-name" title={marca.nombre}>
                          {marca.nombre}
                        </span>
                      </div>
                      <div className="bar-track">
                        <div
                          className={`bar-fill brand-fill ${getRankClass()}`}
                          style={{
                            width: `${porcentaje}%`,
                            animationDelay: `${index * 0.08}s`
                          }}
                        >
                          <span className="bar-value">{Math.round(marca.cantidadTotal)}</span>
                        </div>
                      </div>
                      <div className="bar-stats">
                        <span className="stat-orders">{marca.productosUnicos} productos</span>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3 className="section-title">Acciones Rapidas</h3>
        <div className="actions-grid">
          <button className="action-card" onClick={() => navigate('/estado-cuenta')}>
            <div className="action-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </div>
            <span className="action-label">Descargar Estado de Cuenta</span>
          </button>

          <button className="action-card" onClick={() => navigate('/historial')}>
            <div className="action-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <span className="action-label">Ver Historial de Compras</span>
          </button>

          <button className="action-card" onClick={() => navigate('/configuracion')}>
            <div className="action-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </div>
            <span className="action-label">Configuracion</span>
          </button>
        </div>
      </div>

      {/* Modal de Detalle de Factura */}
      {selectedInvoice && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalle de Factura</h3>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-invoice-info">
              <div className="invoice-info-row">
                <span className="info-key">Folio:</span>
                <span className="info-value">{selectedInvoice.folio}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Fecha:</span>
                <span className="info-value">{formatDate(selectedInvoice.fecha)}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Vencimiento:</span>
                <span className="info-value">{formatDate(selectedInvoice.fechaVencimiento)}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Total:</span>
                <span className="info-value">{formatCurrency(selectedInvoice.total)}</span>
              </div>
              <div className="invoice-info-row">
                <span className="info-key">Saldo Pendiente:</span>
                <span className="info-value text-error">{formatCurrency(selectedInvoice.saldoPendiente)}</span>
              </div>
            </div>

            <div className="modal-lines-section">
              <h4>Productos / Servicios</h4>
              {loadingLines ? (
                <div className="loading-lines">
                  <div className="spinner"></div>
                  <span>Cargando detalle...</span>
                </div>
              ) : invoiceLines.length > 0 ? (
                <div className="lines-table-container">
                  <table className="lines-table">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>Cantidad</th>
                        <th>Precio Unit.</th>
                        <th>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceLines.map((line) => (
                        <tr key={line.id}>
                          <td className="product-name">
                            {Array.isArray(line.product_id) ? line.product_id[1] : line.name}
                          </td>
                          <td className="qty-cell">{line.quantity}</td>
                          <td className="price-cell">{formatCurrency(line.price_unit)}</td>
                          <td className="subtotal-cell">{formatCurrency(line.price_subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-lines">
                  <p>No se encontraron productos en esta factura</p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <Button variant="secondary" onClick={closeModal}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
